import React, { useEffect, useState, useMemo } from 'react';
import { Button, DataGrid } from '@fashionnovateam/nova-ui-toolkit';
import { useSampleTagsListWithSignedUrls } from '../../../Lib/Hooks/sample-tags.hooks';
import { SampleTagResponse, ItemFilters } from '../AddTags/types';
import { possibleParams } from '../AddTags/constants';
import { EmptyListGeneric } from '../../SellToUs/EmptyList';
import { ExtractFilterType } from '../../../Lib/Hooks/Filters/useParamFilter';
import AddTagsDrawer from '../AddTags/AddTagDrawer';
import { usePDFDownload } from '../AddTags/hooks/usePDFDownload';
import { sampleTagsColumns } from './columns';
import { useTransformedRows } from './hooks';

interface SampleTagsListProps {
  userId: string;
  onCreateNew: () => void;
  paramFilters: ExtractFilterType<typeof possibleParams>;
  searchValue?: string;
  domesticMerchPoEligible?: boolean;
  allowedSampleTypes?: ItemFilters[];
}

export function SampleTagsList({
  userId,
  onCreateNew,
  paramFilters,
  searchValue,
  domesticMerchPoEligible,
  allowedSampleTypes,
}: SampleTagsListProps) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [isDuplicateDrawerOpen, setIsDuplicateDrawerOpen] = useState(false);
  const [editTagId, setEditTagId] = useState<string | null>(null);
  const [duplicateTagId, setDuplicateTagId] = useState<string | null>(null);

  const { printPDF } = usePDFDownload(userId);

  useEffect(() => {
    setPage(1);
  }, [paramFilters.type_of_sample_tag, searchValue]);

  const searchParams = useMemo(
    () => ({
      page,
      limit,
      type_of_sample_tag: paramFilters.type_of_sample_tag,
      with_vendor_catalog: false,
      ...(searchValue && {
        [searchValue.toUpperCase().startsWith('PO')
          ? 'po_number'
          : 'style_number']: searchValue,
      }),
    }),
    [page, limit, paramFilters.type_of_sample_tag, searchValue],
  );

  const {
    data: sampleTagsData,
    isLoading,
    error,
    refetch,
  } = useSampleTagsListWithSignedUrls(userId, searchParams);

  const closeEditDrawer = () => {
    setIsEditDrawerOpen(false);
    setEditTagId(null);
  };

  const closeEditDrawerAndRefresh = () => {
    closeEditDrawer();
    refetch();
  };

  const handleEditDrawerClose = (isOpen: boolean) => {
    if (!isOpen) {
      closeEditDrawer();
    }
  };

  const closeDuplicateDrawer = () => {
    setIsDuplicateDrawerOpen(false);
    setDuplicateTagId(null);
  };

  const closeDuplicateDrawerAndRefresh = () => {
    closeDuplicateDrawer();
    refetch();
  };

  const handleDuplicateDrawerClose = (isOpen: boolean) => {
    if (!isOpen) {
      closeDuplicateDrawer();
    }
  };

  const handlePrintClick = async (tagId: string) => {
    const tagData = sampleTagsData?.tags?.find(
      (tag: SampleTagResponse) => tag.id === tagId,
    );

    if (!tagData) {
      console.error('Tag data not found for ID:', tagId);
      return;
    }

    await printPDF(tagData);
  };

  const handleDuplicateClick = (tagId: string) => {
    setDuplicateTagId(tagId);
    setIsDuplicateDrawerOpen(true);
  };

  const handleEditClick = (tagId: string) => {
    setEditTagId(tagId);
    setIsEditDrawerOpen(true);
  };

  const transformedRows = useTransformedRows({
    sampleTagsData,
    userId,
    handleEditClick,
    handleDuplicateClick,
    handlePrintClick,
  });

  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-600 mb-4">
          Failed to load sample tags. Please try again.
        </div>
        <Button onClick={() => refetch()} color="primary">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <>
      {!isLoading && transformedRows.length > 0 && (
        <DataGrid
          rows={transformedRows}
          columns={sampleTagsColumns}
          loading={isLoading}
          autoHeight={false}
          hideRowHover={false}
          paginationMode="server"
          paginationModel={{
            page: page - 1,
            pageSize: limit,
          }}
          onPaginationModelChange={(model: {
            page: number;
            pageSize: number;
          }) => {
            setPage(model.page + 1);
            setLimit(model.pageSize);
          }}
          rowCount={sampleTagsData?.count || 0}
          pageSizeOptions={[]}
        />
      )}

      {!isLoading && transformedRows.length === 0 && (
        <EmptyListGeneric
          iconName="tag"
          title="No sample labels created."
          description={
            <>
              Once your label is created, we will generate a printable sample
              label to attach to the <br /> sample prior to sending it to us.
            </>
          }
          button={{
            onClick: onCreateNew,
            text: 'Create New',
            iconName: 'plus/plus-circle',
          }}
        />
      )}

      <AddTagsDrawer
        isAddTagsOpened={isEditDrawerOpen}
        setIsAddTagsOpened={handleEditDrawerClose}
        domesticMerchPoEligible={domesticMerchPoEligible}
        allowedSampleTypes={allowedSampleTypes}
        editMode
        editTagId={editTagId}
        onEditComplete={closeEditDrawerAndRefresh}
        onCreateNew={onCreateNew}
      />

      <AddTagsDrawer
        isAddTagsOpened={isDuplicateDrawerOpen}
        setIsAddTagsOpened={handleDuplicateDrawerClose}
        domesticMerchPoEligible={domesticMerchPoEligible}
        allowedSampleTypes={allowedSampleTypes}
        duplicateMode
        duplicateTagId={duplicateTagId}
        onDuplicateComplete={closeDuplicateDrawerAndRefresh}
        onCreateNew={onCreateNew}
      />
    </>
  );
}
